import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { IEntitlementState, IPlan, PlanCode } from '@stem/contracts';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';

interface Subscription {
  id: string;
  planCode: PlanCode;
  status: string;
  seats: number;
  seatsUsed: number;
  currentPeriodEnd: string;
  provider: string | null;
}

interface Seat {
  id: string;
  membershipId: string;
  email: string;
  fullName: string;
  role: string;
  revokedAt: string | null;
}

interface Member {
  id: string;
  email: string;
  fullName: string;
  role: string;
}

/** Plans, the current subscription, and who holds a seat. */
export function BillingPage() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const canManage = useSession(
    state => state.permissions.includes('subscription@manage') || state.permissions.includes('*@*'),
  );

  const plans = useQuery({ queryKey: ['plans'], queryFn: () => apiClient.get<IPlan[]>('/v1/billing/plans') });
  const subscription = useQuery({
    queryKey: ['subscription'],
    queryFn: () => apiClient.get<Subscription | null>('/v1/billing/subscription'),
  });
  const entitlements = useQuery({
    queryKey: ['entitlements'],
    queryFn: () => apiClient.get<IEntitlementState>('/v1/billing/entitlements'),
  });
  const seats = useQuery({
    queryKey: ['seats'],
    queryFn: () => apiClient.get<Seat[]>('/v1/billing/seats'),
    enabled: canManage,
  });
  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => apiClient.get<Member[]>('/v1/org/members'),
    enabled: canManage,
  });

  const checkout = useMutation({
    mutationFn: (planCode: PlanCode) =>
      apiClient.post<{ authorizationUrl: string }>('/v1/billing/checkout', {
        planCode,
        seats: 1,
        callbackUrl: `${window.location.origin}/billing/return`,
      }),
    // The browser leaves for Paystack's hosted page; no card detail touches
    // this application at any point.
    onSuccess: data => window.location.assign(data.authorizationUrl),
    onError: error => setMessage((error as Error).message),
  });

  const assignSeat = useMutation({
    mutationFn: (membershipId: string) => apiClient.post(`/v1/billing/seats/${membershipId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['seats'] }),
    onError: error => setMessage((error as Error).message),
  });

  const revokeSeat = useMutation({
    mutationFn: (membershipId: string) => apiClient.delete(`/v1/billing/seats/${membershipId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['seats'] }),
    onError: error => setMessage((error as Error).message),
  });

  const current = subscription.data;
  const seated = new Set((seats.data ?? []).filter(seat => !seat.revokedAt).map(s => s.membershipId));
  const unmetered = current ? current.seats === 0 : false;

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink-900">Membership &amp; subscription</h1>
      <p className="text-sm text-ink-600">
        What your role allows and what your plan includes are two different gates. This is the second
        one.
      </p>

      {message && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{message}</p>}

      <section className="card mt-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Current</h2>
        {current ? (
          <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
            <span className="text-lg font-bold capitalize text-ink-900">{current.planCode}</span>
            <span className="badge bg-emerald-100 text-emerald-800">{current.status}</span>
            <span className="text-ink-600">
              {unmetered
                ? 'Unlimited seats'
                : `${current.seatsUsed} of ${current.seats} seats assigned`}
            </span>
            <span className="text-ink-500">
              Renews {new Date(current.currentPeriodEnd).toLocaleDateString()}
            </span>
          </div>
        ) : (
          <p className="mt-1 text-sm text-ink-600">
            This organisation is on the free membership.
          </p>
        )}

        {entitlements.data && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {entitlements.data.features.length === 0 ? (
              <span className="text-xs text-ink-500">No premium features included.</span>
            ) : (
              entitlements.data.features.map(feature => (
                <span key={feature} className="badge bg-brand-50 text-brand-800">
                  {feature}
                </span>
              ))
            )}
          </div>
        )}
      </section>

      <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(plans.data ?? []).map(plan => (
          <div key={plan.code} className="card flex flex-col gap-2">
            <h3 className="text-base font-semibold text-ink-900">{plan.name}</h3>
            <p className="text-sm text-ink-600">{plan.description}</p>

            <p className="text-lg font-bold text-brand-900">
              {plan.amount === 0
                ? plan.code === 'institutional'
                  ? 'Quoted'
                  : 'Free'
                : `${plan.currency} ${(plan.amount / 100).toLocaleString()}`}
              {plan.amount > 0 && (
                <span className="text-xs font-normal text-ink-500"> / {plan.interval}</span>
              )}
            </p>

            <ul className="space-y-0.5 text-xs text-ink-600">
              {plan.features.slice(0, 6).map(feature => (
                <li key={feature}>· {feature}</li>
              ))}
            </ul>

            <p className="text-xs text-ink-500">
              {plan.seatLimit === null ? 'Unlimited accounts' : `${plan.seatLimit} account(s)`}
            </p>

            {canManage && plan.amount > 0 && (
              <button
                type="button"
                className="btn-primary mt-auto"
                disabled={checkout.isPending || current?.planCode === plan.code}
                onClick={() => checkout.mutate(plan.code)}
              >
                {current?.planCode === plan.code ? 'Current plan' : 'Choose'}
              </button>
            )}
            {plan.code === 'institutional' && (
              <a href="/contact" className="btn-secondary mt-auto">
                Talk to us
              </a>
            )}
          </div>
        ))}
      </section>

      {canManage && current && !unmetered && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-500">Seats</h2>
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-ink-100 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2">Member</th>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Seat</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {(members.data ?? []).map(member => (
                  <tr key={member.id}>
                    <td className="px-4 py-2">
                      {member.fullName}
                      <span className="block text-xs text-ink-500">{member.email}</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{member.role}</td>
                    <td className="px-4 py-2">
                      {seated.has(member.id) ? (
                        <span className="badge bg-emerald-100 text-emerald-800">assigned</span>
                      ) : (
                        <span className="badge bg-ink-200 text-ink-600">none</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {seated.has(member.id) ? (
                        <button
                          type="button"
                          className="text-xs text-red-700 underline"
                          onClick={() => revokeSeat.mutate(member.id)}
                        >
                          Revoke
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="text-xs text-brand-700 underline"
                          onClick={() => assignSeat.mutate(member.id)}
                        >
                          Assign
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
